import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { brandManagerService } from "../../services/companyAuthService";
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiUser, FiEye, FiMail, FiPhone, FiBriefcase } from "react-icons/fi";
import ViewManagerModal from "./ViewManagerModal";

const BrandManagersDashboard = () => {
  const navigate = useNavigate();
  const [brandManagers, setBrandManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    totalManagers: 0,
    activeManagers: 0,
    inactiveManagers: 0
  });
  const [selectedManager, setSelectedManager] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchBrandManagers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await brandManagerService.getBrandManagers(params);
      if (response.success) {
        setBrandManagers(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching brand managers:", error);
      toast.error(error.message || "Failed to fetch brand managers");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBrandManagers();
    }, searchTerm ? 500 : 0);
    return () => clearTimeout(timeoutId);
  }, [fetchBrandManagers, searchTerm]);

  const fetchStats = async () => {
    try {
      const response = await brandManagerService.getBrandManagerStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    try {
      const response = await brandManagerService.deleteBrandManager(id);
      if (response.success) {
        toast.success("Brand manager deleted successfully");
        fetchBrandManagers();
        fetchStats();
      }
    } catch (error) {
      console.error("Error deleting brand manager:", error);
      toast.error(error.message || "Failed to delete brand manager");
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const response = await brandManagerService.updateBrandManagerStatus(id, newStatus);
      if (response.success) {
        toast.success(`Brand manager ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
        fetchBrandManagers();
        fetchStats();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleViewManager = (manager) => {
    setSelectedManager(manager);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedManager(null);
  };

  const filteredManagers = brandManagers.filter(manager => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      manager.name?.toLowerCase().includes(searchLower) ||
      manager.email?.toLowerCase().includes(searchLower) ||
      manager.position?.toLowerCase().includes(searchLower) ||
      manager.department?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <div className="min-h-full bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">Brand Managers</h1>
              <p className="text-[10px] md:text-sm lg:text-base text-gray-600 mt-1">Manage your brand managers and their information</p>
            </div>
            <button
              onClick={() => navigate("/company/dashboard/brands/brand-managers")}
              className="inline-flex items-center px-4 py-2 text-[10px] md:text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200 font-medium"
            >
              <FiPlus className="mr-2" />
              Add Brand Manager
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <div className="bg-white rounded-lg shadow p-6 lg:mb-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Total Managers</p>
                  <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mt-1">{stats.totalManagers}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <FiUser className="text-blue-600 text-xl" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Active Managers</p>
                  <p className="text-lg md:text-xl lg:text-2xl font-bold text-green-600 mt-1">{stats.activeManagers}</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <FiUser className="text-green-600 text-xl" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-gray-600">Inactive Managers</p>
                  <p className="text-lg md:text-xl lg:text-2xl font-bold text-red-600 mt-1">{stats.inactiveManagers}</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <FiUser className="text-red-600 text-xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-2 md:left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, position, or department..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-8 md:pl-10 pr-4 py-2 text-[10px] md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 text-[10px] md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <p className="mt-2 text-xs md:text-sm text-gray-600">Loading brand managers...</p>
              </div>
            ) : filteredManagers.length === 0 ? (
              <div className="p-8 text-center">
                <FiUser className="mx-auto text-gray-400 text-5xl mb-4" />
                <p className="text-gray-600 text-xs md:text-sm">No brand managers found</p>
                <button
                  onClick={() => navigate("/company/dashboard/brands/brand-managers")}
                  className="mt-4 text-orange-500 hover:text-orange-600 font-medium"
                >
                  Add your first brand manager
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto text-[10px] md:text-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Manager
                      </th>
                      <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-2 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredManagers.map((manager) => (
                      <tr key={manager._id} className="hover:bg-gray-50">
                        <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <span className="text-orange-600 font-semibold">
                                {manager.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-xs md:text-sm font-medium text-gray-900">{manager.name}</div>
                              {manager.employeeId && (
                                <div className="text-xs md:text-sm text-gray-500">ID: {manager.employeeId}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center mb-1">
                            <FiMail className="mr-2 text-gray-400" />
                            {manager.email}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 flex items-center">
                            <FiPhone className="mr-2 text-gray-400" />
                            {manager.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs md:text-sm text-gray-900 flex items-center">
                            <FiBriefcase className="mr-2 text-gray-400" />
                            {manager.position}
                          </div>
                          {manager.reportingTo && (
                            <div className="text-xs md:text-sm text-gray-500 mt-1">
                              Reports to: {manager.reportingTo}
                            </div>
                          )}
                        </td>
                        <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                          <div className="text-xs md:text-sm text-gray-900">
                            {manager.department || "-"}
                          </div>
                        </td>
                        <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleStatusToggle(manager._id, manager.status)}
                            className={`inline-flex px-2 py-1 text-[10px] md:text-sm font-semibold rounded-full ${
                              manager.status === "active"
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            } transition-colors`}
                          >
                            {manager.status === "active" ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-2 md:px-6 py-4 whitespace-nowrap text-right text-xs md:text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleViewManager(manager)}
                              className="text-green-600 hover:text-green-900 p-2 text-[10px] md:text-sm hover:bg-green-50 rounded transition-colors"
                              title="View Details"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => navigate(`/company/dashboard/brands/brand-managers/edit/${manager._id}`)}
                              className="text-blue-600 hover:text-blue-900 p-2 text-[10px] md:text-sm hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => handleDelete(manager._id, manager.name)}
                              className="text-red-600 hover:text-red-900 p-2 text-[10px] md:text-sm hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Manager Modal */}
      <ViewManagerModal
        isOpen={isModalOpen}
        manager={selectedManager}
        onClose={handleCloseModal}
      />

      <ToastContainer />
    </>
  );
};

export default BrandManagersDashboard;

